import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/apiClient";
import { PublicHeader, PublicFooter } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function audienceBlockFromError(e) {
  const d = e?.response?.data?.detail;
  if (e?.response?.status === 403 && d && typeof d === "object" && d.code === "audience_blocked") {
    return d;
  }
  return null;
}

export default function ClaimDiscount() {
  const { code } = useParams();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [block, setBlock] = useState(null);

  const submit = async () => {
    setLoading(true);
    setBlock(null);
    try {
      const { data } = await api.post(`/r/${code}/claim`, { customer_phone: phone });
      if (data.warning) toast.warning(data.warning);
      nav(`/redeem/${data.redemption_code}`);
    } catch (e) {
      const blocked = audienceBlockFromError(e);
      if (blocked) setBlock(blocked);
      else toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <PublicHeader />
      <div className="mx-auto max-w-md flex-1 px-4 py-16">
        {block ? (
          <div data-testid="audience-block-screen">
            <h1 className="text-3xl font-black tracking-tight" data-testid="audience-block-title">
              {block.title}
            </h1>
            <p className="mt-3 text-muted-foreground" data-testid="audience-block-message">
              {block.message}
            </p>
            <div className="mt-8 flex flex-col gap-2">
              <Button
                className="w-full bg-brand-green hover:bg-brand-green-dark"
                onClick={() => nav("/")}
                data-testid="audience-block-browse"
              >
                Browse businesses
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setBlock(null); setPhone(""); }}
                data-testid="audience-block-try-again"
              >
                Try a different number
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-black tracking-tight">Claim your discount</h1>
            <p className="mt-2 text-muted-foreground">
              Enter your phone number to get your redemption code. We use it only to match your referral — no account needed.
            </p>
            <div className="mt-8 space-y-3">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Phone number</label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                data-testid="claim-phone-input"
                className="text-lg"
              />
              <Button
                className="w-full bg-brand-green hover:bg-brand-green-dark"
                disabled={loading || phone.length < 7}
                onClick={submit}
                data-testid="claim-submit-btn"
              >
                {loading ? "Issuing code…" : "Get my code"}
              </Button>
            </div>
          </>
        )}
      </div>
      <PublicFooter />
    </div>
  );
}
